import { ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks/useCurrentUser';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

export const JobDetailsFragment = graphql(`
  fragment JobDetails on Job {
    id
    pool_id
    track_unpaid_breaks
    estimated_unpaid_break_time_minutes
    cancellation_fee_window
    cancellation_fee_charge_hours
    starts_date
    ends_time
    detail
    scrubs_provided
    shift_type
    posting_field_contents
    requires_sca_onboarding_condition
    total_payout_bonus_cents
    status
    new_to_facility
    requested_by_facility(professional_id: $professional_id)
    can_book_job
    pro_confirmed_at
    patient_population
    current_starts_time
    current_ends_time
    starts_time
    guaranteed_minimum_hours
    booked_at
    approved_clock_in
    approved_clock_out
    updated_in_cancellation_window
    location_id
    assignment_id
    pending_shift_request {
      id
      status
      request_kind
      expires_at
      requester_account_info {
        name
        professional_id
      }
      requestee_account_info {
        professional_id
      }
    }
    latest_declined_shift_request {
      id
      status
      request_kind
      expires_at
      reason
      decided_at
    }
    latest_approved_shift_request {
      id
      status
      request_kind
      expires_at
      requester_account_info {
        name
        professional_id
      }
    }
    surgery_types {
      display_name
      id
    }
    expense_adjustments {
      amount_cents
      id
      taxable_type
      payout {
        amount_cents
        id
        status
        processed_on
        w2_submit_by_time
      }
    }
    job_billing_sums {
      payout_total_amount_cents
      total_hours
      payout_total_amount_cents
      total_breaks_minutes
      total_hours
    }
    assignment {
      id
      is_w2
      track_unpaid_breaks
    }
    is_w2
    facility_number
    clinical_contact {
      first_name
      last_name
    }
    job_profile_for_account_id {
      active
      payout_facility_bonus_cents
      payout_kind
      payout_max_daily_ot_hours
      payout_max_daily_regular_hours
      payout_max_weekly_regular_hours
      payout_base_hourly_rate_cents
      payout_double_overtime_multiplier
      payout_on_call_hourly_rate_cents
      payout_overtime_multiplier
      payout_taxable_hourly_rate_cents
    }
    specialties {
      id
      label
    }
    positions {
      name
      high_risk
      display_name
    }
    location {
      allow_swap_window_hours
      allow_release_window_hours
      allow_drop_window_hours
      swap_approval_window_hours
      release_approval_window_hours
      drop_approval_window_hours
      shift_swap_enabled
      shift_release_enabled
      shift_drop_enabled
      start_clock_in_early_window
      market {
        display_name
      }
      ehr_system {
        label
      }
      ehr_details
      state_id
      approval_free_late_clock_out_grace_period_minutes
      maximum_late_clock_out_grace_period_minutes
      require_facility_timecard_system_photo_on_clock_out
      ignore_signature_when_creating_meal_break
      professional_can_adjust_times
      medication_management_details
      scrub_colors
      meal_break_details
      additional_check_in_instructions
      supplies_ppe_details
      require_upload_on_clock_out
      parking_instructions
      parking_cost_cents
      parking_reimbursed
      parking_reimbursement_cents
      parking_free
      parking_validated
      company_id
      address_city
      address_zip
      address_first
      full_address
      location_type {
        label
      }
      state {
        abbreviation
        covid_requirement
      }
      covid_requirement
      clock_in_early_window
      enable_geo_fence
      timezone_lookup
      name
      coordinates {
        latitude
        longitude
      }
    }
    disputes {
      id
      status
    }
    job_billing_sums {
      payout_total_amount_cents
      payout_callback_amount_cents
      payout_callback_hours
      payout_regular_amount_cents
      payout_regular_hours
      payout_overtime_amount_cents
      payout_overtime_hours
      payout_daily_overtime_amount_cents
      payout_daily_overtime_hours
      payout_weekly_overtime_amount_cents
      payout_weekly_overtime_hours
      payout_double_amount_cents
      payout_double_hours
      total_on_call_hours
    }
    job_billings {
      id
      category
      payout_total_amount_cents
      payout {
        amount_cents
        id
        status
        processed_on
        w2_submit_by_time
      }
      disputed_job_billings {
        id
      }
    }
    missing_high_risk_specialties {
      specialty_id
      label
    }
  }
`);

const AvailableJobGql = graphql(`
  query GetAvailableJobForAvailableJobDetailsContainer($id: Int!, $professional_id: Int!) {
    availableJob(id: $id) {
      id
      status
      pending_shift_request {
        id
        status
      }
      ...JobDetails
    }
  }
`);

type AvailableJobProps = ResultOf<typeof AvailableJobGql>;

const JobGql = graphql(`
  query GetJobForJobDetailsContainer($id: Int!, $professional_id: Int!) {
    job(id: $id) {
      id
      status
      professional_id
      starts_time
      location {
        shift_release_enabled
        shift_drop_enabled
        shift_swap_enabled
        allow_drop_window_hours
        allow_swap_window_hours
        allow_release_window_hours
      }
      pending_shift_request {
        id
        status
      }
      latest_approved_shift_request {
        id
        status
        request_kind
        requester_account_info {
          name
          professional_id
        }
      }
      ...JobDetails
    }
  }
`);

type JobProps = ResultOf<typeof JobGql>;

export const useAvailableJobForJobDetailsContainer = (id: number) => {
  const { currentUser } = useCurrentUser();
  const professional_id = currentUser?.professional?.id;

  const { data, isLoading, refetch } = useGraphQLQuery<AvailableJobProps>({
    operationName: 'GetAvailableJobForAvailableJobDetailsContainer',
    queryKey: ['availableJob', id, professional_id],
    query: AvailableJobGql,
    variables: { id, professional_id },
    enabled: !!id,
  });
  const availableJob = data?.availableJob;

  return { availableJob, isLoading, refetch };
};

export const useJobForJobDetailsContainer = (id: number) => {
  const { currentUser } = useCurrentUser();
  const professional_id = currentUser?.professional?.id;

  const { data, isLoading, isFetching, refetch } = useGraphQLQuery<JobProps>({
    operationName: 'GetJobForJobDetailsContainer',
    queryKey: ['job', id, professional_id],
    query: JobGql,
    variables: { id, professional_id },
    enabled: !!id,
  });
  const job = data?.job;

  return { job, isLoading: isLoading || isFetching, refetch };
};
